'use client'

import { formatPercentage } from '@/domain/common/format'
import type { NormalizedUnitNumber, Percentage } from '@/domain/types/NumericValues'
import { Skeleton } from '@/ui/atoms/skeleton/Skeleton'
import { Typography } from '@/ui/atoms/typography/Typography'
import { Info } from '@/ui/molecules/info/Info'
import { formatTVL } from '../utils/formatTVL'
interface UsdxlCardContentProps {
  isLoading: boolean
  usdxlPrice?: NormalizedUnitNumber
  totalSupply?: NormalizedUnitNumber
  borrowAPY?: Percentage
  poolTotalApr?: Percentage
  balancerPoolApr?: Percentage
}

export function UsdxlCardContent({
  isLoading,
  usdxlPrice,
  totalSupply,
  borrowAPY,
  poolTotalApr,
  balancerPoolApr,
}: UsdxlCardContentProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-4 lg:flex lg:flex-wrap lg:gap-8">
        <div className="flex flex-col gap-1">
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-3 w-12" />
        </div>
        <div className="flex flex-col gap-1">
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-3 w-12" />
        </div>
        <div className="flex flex-col gap-1">
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-3 w-16" />
        </div>
        <div className="flex flex-col gap-1">
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-3 w-14" />
        </div>
      </div>
    )
  }

  const price = usdxlPrice ? `$${Number(usdxlPrice).toFixed(4)}` : '--'
  const supply = totalSupply ? formatTVL(Number(totalSupply)) : '--'
  const apy = borrowAPY ? `${(Number(borrowAPY) * 100).toFixed(2)}%` : '--'
  const hasPoolTotalApr = poolTotalApr && Number(poolTotalApr) > 0

  return (
    <div className="grid grid-cols-2 gap-4 lg:flex lg:flex-wrap lg:gap-8">
      <div className="flex flex-col">
        <span className="font-semibold text-white text-xl">{price}</span>
        <span className="text-[11px] text-muted-foreground">Price</span>
      </div>
      <div className="flex flex-col">
        <span className="font-semibold text-white text-xl">{supply}</span>
        <span className="text-[11px] text-muted-foreground">Supply</span>
      </div>
      <div className="flex flex-col">
        <span className="font-semibold text-white text-xl">{apy}</span>
        <span className="text-[11px] text-muted-foreground">Borrow APY</span>
      </div>
      <div className="flex flex-col">
        {hasPoolTotalApr ? (
          <Typography variant="span" gradient className="font-semibold text-xl" style={{ paddingBottom: '0' }}>
            {formatPercentage(poolTotalApr)}
          </Typography>
        ) : (
          <span className="font-semibold text-white text-xl">--</span>
        )}
        <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
          <span className="text-muted-foreground leading-non">Pool APR</span>
          {hasPoolTotalApr && (
            <Info>
              <div className="flex flex-col gap-3 text-sm">
                <Typography variant="p" className="text-sm">
                  Total APR is sourced from Balancer.
                </Typography>
                <div className="flex items-center justify-between gap-4">
                  <Typography variant="prompt" className="text-sm">
                    Balancer pool APR:
                  </Typography>
                  <Typography variant="p" className="font-bold text-foreground text-sm">
                    {formatPercentage(balancerPoolApr)}
                  </Typography>
                </div>
              </div>
            </Info>
          )}
        </span>
      </div>
    </div>
  )
}
